import { ArrowRight } from 'lucide-react';
import { AuthPrimaryButton } from '../../../Authentication/AuthControls';

/**
 * What Polar's hosted checkout takes for a subscription: cards, and the Apple Pay and Google Pay wallets where the
 * buyer's device has them (Polar's payment methods page names no card brands, so none is named here). Written
 * out, not drawn: nothing in the app carries the brands' artwork.
 */
export const PAYMENT_METHODS_NOTE =
  'Pay by credit or debit card, or with Apple Pay or Google Pay where your device supports it. Secure checkout by Polar.';

export type TimelineRow = { when: string; what: string; amount: string; emphasis?: boolean };

type PlanSummaryProps = {
  label: string;
  title: string;
  line: string;
  rows: TimelineRow[];
  ctaLabel: string;
  note: string;
  /** The button opens a checkout, so say how it can be paid. Off for a change that uses the card already on file. */
  showPaymentMethods?: boolean;
  disabled: boolean;
  isPending: boolean;
  onContinue: () => void;
  /** A quiet way out beside the main button, such as backing out of a plan change. */
  secondary?: { label: string; onClick: () => void };
};

/**
 * The blue panel under the plans: what is chosen, what happens when (a dated timeline) and
 * the one button that accepts it: the sign-up's trial, and a plan change in Settings. The same tinted surface as the sign-in chat.
 */
export const PlanSummary = ({
  label,
  title,
  line,
  rows,
  ctaLabel,
  note,
  showPaymentMethods = false,
  disabled,
  isPending,
  onContinue,
  secondary,
}: PlanSummaryProps) => (
  <aside
    aria-label={label}
    className="border-accent-line flex flex-col gap-[22px] rounded-[16px] border bg-[color-mix(in_oklch,hsl(var(--accent))_7%,hsl(var(--panel)))] px-6 py-7 md:px-8"
  >
    <div className="flex flex-col gap-2.5">
      <span className="micro-label">{label}</span>
      <h2 className="text-foreground m-0 text-[clamp(26px,2.6vw,34px)] leading-[1.06] font-medium tracking-[-0.035em] text-balance">
        {title}
      </h2>
      <p className="text-dim m-0 text-[13px] leading-[1.6]">{line}</p>
    </div>

    <div className="bg-panel rounded-xl px-4 py-1.5">
      {rows.map((row, index) => (
        <div
          key={`${row.when}-${row.what}`}
          className={`grid grid-cols-[72px_1fr_auto] items-baseline gap-3 py-[11px] ${index < rows.length - 1 ? 'border-line border-b' : ''}`}
        >
          <span className={`log-line ${row.emphasis ? 'text-accent font-medium' : 'text-dim'}`}>{row.when}</span>
          <span className="text-foreground text-micro">{row.what}</span>
          <span className="log-line text-foreground">{row.amount}</span>
        </div>
      ))}
    </div>

    <div className="flex flex-col gap-2.5">
      <AuthPrimaryButton
        type="button"
        onClick={onContinue}
        disabled={disabled}
        isPending={isPending}
        trailingIcon={<ArrowRight className="size-4" strokeWidth={1.5} />}
      >
        {ctaLabel}
      </AuthPrimaryButton>
      {secondary && (
        <button
          type="button"
          onClick={secondary.onClick}
          disabled={isPending}
          className="text-dim hover:text-foreground focus-visible:ring-ring self-center rounded-sm text-micro underline underline-offset-4 focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60"
        >
          {secondary.label}
        </button>
      )}
      <span className="text-dim text-center text-nano leading-normal">{note}</span>
      {showPaymentMethods && (
        <span className="text-dim text-center text-nano leading-normal">{PAYMENT_METHODS_NOTE}</span>
      )}
    </div>
  </aside>
);
